'use client';

import * as DS from '@aurorasolar/ds';
import { type StaticImageData } from 'next/image';

import { LocationSubmit } from '@/app/components';
import { PostSummary } from '@/app/home-solar/blog/actions';
// Deep import: the blog barrel imports this lp barrel, and a barrel-to-barrel
// cycle TDZ-crashes the webpack (Storybook) build
import { BlogSlideshow } from '@/app/home-solar/blog/components/BlogSlideshow';
import { TRACKING } from '@/helpers/tracking';

import { coupleInBackyard, sampleQuotes, seniorCoupleReviewingBills } from '../assets';

import { ButtonLink } from './ButtonLink';
import { CtaSection } from './CtaSection';
import { H2 } from './H2';
import { PageHero } from './PageHero';
import { Section } from './Section';
import { TrustIndicators } from './TrustIndicators';

const SECTION1_HEADLINE = 'No jargon. No pressure.';
const SECTION2_HEADLINE = 'Your data stays private until you choose to share';
const SECTION2_BODY =
  "We don't gather your information until you decide if and when to share your details with a solar professional.";
const CTA_BANNER_HEADLINE = 'Get updates in your inbox';
const SLIDESHOW_TITLE = 'Explore solar guides and insights';
const LEARN_MORE_PATH = '/home-solar/blog/solar-101/how-to-buy-solar-with-confidence';

type Props = {
  heroBgImg: StaticImageData;
  heroBody: string;
  heroHeadline: string;
  section1Body: string;
  slideshowPosts: PostSummary[];
};

export const StateLandingPage = ({ heroBgImg, heroBody, heroHeadline, section1Body, slideshowPosts }: Props) => (
  <article>
    <PageHero
      bgImg={heroBgImg}
      headline={heroHeadline}
      heroImage={{ alt: 'Sample installer estimates', src: sampleQuotes }}
      subheadline={heroBody}
    >
      <DS.Flex gap={2} maxW={500} mx="auto">
        <LocationSubmit variant="cta">Get estimates</LocationSubmit>
        <TrustIndicators centered />
      </DS.Flex>
    </PageHero>

    <Section imageRight={{ alt: 'Solar professional with homeowners', height: 500, src: coupleInBackyard }}>
      <H2>{SECTION1_HEADLINE}</H2>
      <p>{section1Body}</p>
      <ButtonLink
        event={{ data: { page: LEARN_MORE_PATH }, name: TRACKING.BLOG_READ_MORE }}
        icon={DS.IconArrowRight}
        iconPosition="right"
        path={LEARN_MORE_PATH}
        size="md"
        variant="cta"
      >
        Learn more
      </ButtonLink>
    </Section>

    <Section imageLeft={{ alt: 'Couple reviewing documents on laptop', height: 500, src: seniorCoupleReviewingBills }}>
      <H2>{SECTION2_HEADLINE}</H2>
      <p>{SECTION2_BODY}</p>
      <ButtonLink
        event={{ name: TRACKING.LANDING_CLICK_GET_QUOTE }}
        icon={DS.IconArrowRight}
        iconPosition="right"
        path="/quote-builder"
        size="md"
        variant="cta"
      >
        Start spam free
      </ButtonLink>
    </Section>

    <CtaSection action="email">{CTA_BANNER_HEADLINE}</CtaSection>

    <BlogSlideshow posts={slideshowPosts} title={SLIDESHOW_TITLE} />
  </article>
);
